import { ContentContainer } from '@/components/ui/content-container';
import { Heading2 } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import type { ReviewWidgetProps } from '../definition';
import {
  REVIEW_ALIGN_ITEMS_CLASS,
  type ReviewAlign,
  type ReviewDisplaySettings,
  resolveReviewSegments,
  resolveReviewSizeClass,
} from './review-display';
import { ReviewAttribution, ReviewQuote, ReviewStars } from './shared';

// Gap controls the vertical rhythm between rows (the list has no card box).
const LIST_GAP_CLASS = { sm: 'py-4', md: 'py-7', lg: 'py-10' } as const;
const LIST_JUSTIFY_CLASS: Record<ReviewAlign, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
};

export function ListVariant({
  title,
  reviews,
  settings,
}: ReviewWidgetProps & { settings?: ReviewDisplaySettings }) {
  if (reviews.length === 0) return null;

  const { showIcon, showName, segments } = resolveReviewSegments(settings, 'list');
  const rowSpacingClass = resolveReviewSizeClass(settings?.gap, LIST_GAP_CLASS, 'md');
  // The list is centered by default; only override when an alignment is set.
  const itemsClass = settings?.align
    ? REVIEW_ALIGN_ITEMS_CLASS[settings.align]
    : 'items-center text-center';
  const justifyClass = settings?.align ? LIST_JUSTIFY_CLASS[settings.align] : 'justify-center';

  return (
    <ContentContainer
      passthrough
      className="mx-auto flex w-full max-w-[90rem] flex-col items-center gap-16 px-8 section-padding"
    >
      {title ? <Heading2 className="text-center">{title}</Heading2> : null}

      <div className="mx-auto flex w-full max-w-4xl flex-col">
        {reviews.map((review, index) => {
          return (
            <article
              key={review.id || index}
              className={cn(
                'border-b border-slate-200 first:pt-0 last:border-b-0',
                rowSpacingClass,
              )}
            >
              <div className={cn('flex flex-1 flex-col', itemsClass)}>
                {segments.map((segment) => {
                  if (segment === 'rating') {
                    return (
                      <ReviewStars
                        key="rating"
                        review={review}
                        className={cn('mt-4 first:mt-0', justifyClass)}
                      />
                    );
                  }
                  if (segment === 'review') {
                    return (
                      <ReviewQuote key="review" review={review} bold className="mt-4 first:mt-0" />
                    );
                  }
                  return (
                    <div key="attribution" className={cn('mt-5 flex first:mt-0', justifyClass)}>
                      <ReviewAttribution
                        review={review}
                        compact
                        showIcon={showIcon}
                        showName={showName}
                      />
                    </div>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>
    </ContentContainer>
  );
}
